/**
 * Autofill Detection
 *
 * Detects browser autofill by measuring the gap between input events.
 * Autofill fires multiple onChange events in rapid succession (< 20ms gaps),
 * which is impossible for human typing. When detected, the caller can
 * auto-submit the field.
 *
 * Extracted from ChatInputSharing for reuse across overlay inputs.
 */

// Minimum time after mount before detection activates (prevents false positives
// from initial render or controlled value sync)
const MOUNT_DELAY_MS = 500;

// Average gap threshold — autofill fires events < 20ms apart
const AUTOFILL_GAP_MS = 20;

/**
 * Create an autofill detector for a single input field.
 * Returns an object with track() and isReady() methods.
 *
 * @param {number} mountedAt - Date.now() when the component mounted
 * @returns {{ track: (value: string) => void, wasAutofilled: () => boolean }}
 */
export const createAutofillDetector = (mountedAt) => {
  const timestamps = [];

  return {
    // Call on every onChange — records the timestamp
    track() {
      timestamps.push(Date.now());
      if (timestamps.length > 10) timestamps.shift();
    },

    // Returns true if the recent input pattern looks like autofill
    wasAutofilled() {
      if (Date.now() - mountedAt < MOUNT_DELAY_MS) return false;
      if (timestamps.length < 3) return false;
      const gaps = [];
      for (let i = 1; i < timestamps.length; i++) {
        gaps.push(timestamps[i] - timestamps[i - 1]);
      }
      const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      return avgGap < AUTOFILL_GAP_MS;
    },

    // Reset after auto-submit
    reset() {
      timestamps.length = 0;
    },
  };
};
